/* eslint-disable react/no-array-index-key */
'use client';

import { Link } from '@packages/modules/src/Link/Link';
import type { CustomerRatingsHistogramFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { CustomerRatingMeter } from '../CustomerRatingMeter/CustomerRatingMeter';
import { CustomerRatingMeterLabel } from '../CustomerRatingMeterLabel/CustomerRatingMeterLabel.client';
import { useCustomerRatingsAndReviewPage } from '../../../CustomerRatingsAndReviewsPage/hooks/useCustomerRatingsAndReviewPage/useCustomerRatingsAndReviewPage';
import { CustomerRatingMeterCount } from '../CustomerRatingMeterCount/CustomerRatingMeterCount';
import { useCustomerRatingsTracking } from '../../../Tracking/hooks/useCustomerRatingsTracking/useCustomerRatingsTracking';
import { useCustomerRatingsHistogramMessages } from '../useCustomerRatingsHistogramMessages/useCustomerRatingsHistogramMessages';

export type CustomerRatingsHistogramWithLinksProps = {
  reviewStatistics: CustomerRatingsHistogramFragmentFragment['reviewStatistics'];
};

/**
 * The CustomerRatingsHistogramWithLinks component displays a visual histogram of how many ratings are available for a specific rating
 *
 * @param reviewStatistics - The review statistics data for the histogram
 * @returns A React component rendering the customer ratings histogram with links
 */
export const CustomerRatingsHistogramWithLinks = ({
  reviewStatistics,
}: CustomerRatingsHistogramWithLinksProps) => {
  const { getUrl, getArticleId } = useCustomerRatingsAndReviewPage();
  const { getLinkLabel } = useCustomerRatingsHistogramMessages();
  const { trackFilterByRatingValue } = useCustomerRatingsTracking();

  const articleId = getArticleId();

  if (articleId === null) {
    return null;
  }

  return (
    <div className="grid grid-cols-[auto_1fr_auto] gap-y-1">
      {reviewStatistics.map(({ rating, portion, count }, index) => {
        const url = getUrl({
          articleId,
          rating: rating.toString(),
        });

        return (
          <Link
            href={url}
            className="col-span-3 grid grid-cols-subgrid items-center gap-x-1.5"
            title={getLinkLabel(rating, count)}
            onClick={() => {
              trackFilterByRatingValue(rating);
            }}
            rel="nofollow"
            key={`customerRatingHistogramWithLink-${index}`}
          >
            <CustomerRatingMeterLabel rating={rating} />
            <CustomerRatingMeter portion={portion} />
            <CustomerRatingMeterCount ratingsCount={count} />
          </Link>
        );
      })}
    </div>
  );
};
